import React from "react";
import { MapPin, Navigation, Route, Clock, Paperclip, GripVertical, Check, X } from "lucide-react";
import {
  sLabel, sColor, mapsUrl,
  getPrimaryStopDetails, getPrimaryStopSummary,
  downloadPdf, getReadyBadgeLabel, getDdtBadgeLabel,
  formatTimeIT,
} from "../helpers";
import { UrgencyBadge } from "../UrgencyBadge";
import { BarcodeScanner } from "../BarcodeScanner";
import { DriverHeader } from "../DriverHeader";
import { MultiClientWarningDialog, ShiftConfirmDialog, SlideToConfirm, PushHelpModal } from "../dialogs";

export function HomeScreen(ctx) {
  const {
    driverInfo, work, active, completed, sortedActive, nextJob, nextDist, nextEta,
    myLocation, orderVersion,
    shiftStatus, shiftStartTime, breakCount,
    multiSelectMode, multiSelectedIds,
    showMultiPodWarning, pendingMultiPodJobs,
    showScanner, setShowScanner, showPushHelp, setShowPushHelp,
    confirmEvent, setConfirmEvent, showEndShiftSlide, setShowEndShiftSlide,
    pushEnabled, installPrompt, setInstallPrompt,
    etaBadge, handleScan, navigateTo, togglePush, logout,
    requestTimesheetEvent, recordTimesheetEvent,
    setMultiSelectMode, setMultiSelectedIds, toggleMultiSelect, exitMultiSelect,
    startMultiPod, proceedWithMultiPod, setShowMultiPodWarning, setPendingMultiPodJobs,
  } = ctx;

  const nextStopDetails = nextJob ? getPrimaryStopDetails(nextJob) : null;
  const nextStop = nextJob ? getPrimaryStopSummary(nextJob) : null;

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="driver-app" key={orderVersion}>
      {showScanner && <BarcodeScanner onScan={handleScan} onClose={() => setShowScanner(false)} />}
      {showPushHelp && <PushHelpModal onClose={() => setShowPushHelp(false)} />}
      {showMultiPodWarning && (
        <MultiClientWarningDialog
          jobs={pendingMultiPodJobs}
          onCancel={() => { setShowMultiPodWarning(false); setPendingMultiPodJobs([]); }}
          onConfirm={() => {
            const jobs = pendingMultiPodJobs;
            setShowMultiPodWarning(false);
            setPendingMultiPodJobs([]);
            proceedWithMultiPod(jobs);
          }}
        />
      )}
      {confirmEvent && (
        <ShiftConfirmDialog
          event={confirmEvent}
          onConfirm={() => { recordTimesheetEvent(confirmEvent.type); setConfirmEvent(null); }}
          onCancel={() => setConfirmEvent(null)}
        />
      )}
      {showEndShiftSlide && (
        <SlideToConfirm
          label="Trascina per terminare il turno"
          onConfirm={() => { recordTimesheetEvent("end_shift"); setShowEndShiftSlide(false); }}
          onCancel={() => setShowEndShiftSlide(false)}
        />
      )}
      <DriverHeader driverInfo={driverInfo} navigateTo={navigateTo} togglePush={togglePush} pushEnabled={pushEnabled} setShowScanner={setShowScanner} installPrompt={installPrompt} setInstallPrompt={setInstallPrompt} logout={logout} />

      {/* Welcome & Summary */}
      <div className="bg-[#09090B] text-white px-4 pb-6 pt-2">
        <p className="text-lg font-bold">Benvenuto, {driverInfo.name.split(" ")[0]}</p>
        <p className="text-zinc-500 text-xs mt-0.5">Riepilogo di oggi</p>

        <div className="mt-3 bg-zinc-900 border border-zinc-800 rounded-lg p-3" data-testid="shift-control-bar">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${shiftStatus === "working" ? "bg-green-500 animate-pulse" : shiftStatus === "break" ? "bg-amber-400 animate-pulse" : "bg-zinc-600"}`} />
              <span className="text-xs font-bold uppercase tracking-wider">
                {shiftStatus === "working" ? "In servizio" : shiftStatus === "break" ? "In pausa" : "Fuori servizio"}
              </span>
            </div>
            {shiftStartTime && (
              <span className="text-[10px] text-zinc-500">Inizio: {formatTimeIT(shiftStartTime)}</span>
            )}
          </div>
          <div className="flex gap-2">
            {shiftStatus === "off" && (
              <button onClick={() => requestTimesheetEvent("start_shift")} className="flex-1 h-10 bg-green-600 text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="start-shift-btn">
                <Clock size={14} /> Inizia Turno
              </button>
            )}
            {shiftStatus === "working" && (
              <>
                <button onClick={() => requestTimesheetEvent("start_break")} className="flex-1 h-10 bg-amber-500 text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="start-break-btn">
                  <Clock size={14} /> Pausa
                </button>
                <button onClick={() => requestTimesheetEvent("end_shift")} className="flex-1 h-10 bg-red-600 text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="end-shift-btn">
                  <Clock size={14} /> Fine Turno
                </button>
              </>
            )}
            {shiftStatus === "break" && (
              <button onClick={() => requestTimesheetEvent("end_break")} className="flex-1 h-10 bg-green-600 text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1.5" data-testid="end-break-btn">
                <Clock size={14} /> Fine Pausa
              </button>
            )}
          </div>
          {breakCount > 0 && shiftStatus !== "off" && (
            <p className="text-[10px] text-zinc-500 mt-1.5 text-center">{breakCount} {breakCount === 1 ? "pausa" : "pause"} registrate oggi</p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded p-3 text-center">
            <p className="text-2xl font-bold text-[#FF5200]">{active.length}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Da fare</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded p-3 text-center">
            <p className="text-2xl font-bold text-green-500">{completed.length}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Chiuse oggi</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded p-3 text-center">
            <p className="text-2xl font-bold text-blue-400">{work.shipments.length + work.pickups.length}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Totale</p>
          </div>
        </div>
      </div>

      {/* Next Delivery Card */}
      {nextJob && (
        <div className="mx-4 -mt-2 bg-white border border-zinc-200 rounded-lg shadow-sm p-4" data-testid="next-delivery-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#FF5200]">{nextJob._type === "shipment" ? "Prossima consegna" : "Prossimo ritiro"}</p>
            <div className="flex items-center gap-1.5">
              <UrgencyBadge job={nextJob} size="lg" context="next" />
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase text-white rounded-full ${sColor(nextJob.status)}`}>{sLabel(nextJob.status)}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-[#09090B]" data-testid="next-job-primary-address">{nextStop?.title}</p>
              <p className="text-xs text-zinc-600">{nextStop?.locality}</p>
              <p className="text-[11px] text-zinc-400">{nextStop?.subtitle}</p>
              <p className="text-[10px] text-zinc-400 mt-1">{nextJob._type === "shipment" ? "Spedizione" : "Ritiro"} · {nextJob.tracking_number}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {getReadyBadgeLabel(nextJob) && <span className="px-2 py-1 bg-green-50 text-green-700 text-[11px] font-bold rounded-sm" data-testid={`driver-ready-badge-${nextJob.tracking_number}`}>{getReadyBadgeLabel(nextJob)}</span>}
                {getDdtBadgeLabel(nextJob) && <button type="button" onClick={() => downloadPdf(nextJob.ddt_files[0])} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 text-[11px] font-bold rounded-sm hover:bg-zinc-200" data-testid={`driver-ddt-badge-${nextJob.tracking_number}`}><Paperclip size={12} /> {getDdtBadgeLabel(nextJob)}</button>}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-600">
                {nextDist && <span className="flex items-center gap-1"><Navigation size={12} className="text-[#FF5200]" /> {nextDist < 1 ? `${Math.round(nextDist * 1000)}m` : `${nextDist.toFixed(1)} km`}</span>}
                {nextEta && <span className="flex items-center gap-1"><Clock size={12} className="text-zinc-400" /> ~{nextEta} min</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => { navigateTo("detail", nextJob); }} className="flex-1 h-10 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1" data-testid="open-next-job">
              <Route size={14} /> Vai alla consegna
            </button>
            {nextStopDetails && (
              <a href={mapsUrl(nextStopDetails, myLocation ? [myLocation.lat, myLocation.lng] : null)} target="_blank" rel="noopener noreferrer" className="h-10 px-3 bg-[#09090B] text-white text-xs font-bold uppercase rounded-sm flex items-center justify-center gap-1" data-testid="navigate-next">
                <Navigation size={14} />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3 p-4">
        <button onClick={() => navigateTo("list")} disabled={sortedActive.length === 0}
          className="h-14 bg-[#FF5200] text-white font-bold text-sm uppercase rounded-sm flex items-center justify-center gap-2 disabled:opacity-40" data-testid="edit-route-btn">
          <GripVertical size={18} /> Modifica Giro
        </button>
        <button
          onClick={() => {
            if (multiSelectMode) { exitMultiSelect(); }
            else { setMultiSelectMode(true); setMultiSelectedIds([]); }
          }}
          disabled={sortedActive.length === 0}
          className={`h-14 font-bold text-sm uppercase rounded-sm flex items-center justify-center gap-2 disabled:opacity-40 ${multiSelectMode ? "bg-red-600 text-white" : "bg-[#09090B] text-white"}`}
          data-testid="multi-pod-toggle-btn"
        >
          {multiSelectMode ? <><X size={18} /> Annulla</> : <><Check size={18} /> Consegna Multipla</>}
        </button>
      </div>

      {/* Active Deliveries — full list */}
      {sortedActive.length > 0 && (
        <div className={`px-4 ${multiSelectMode && multiSelectedIds.length > 0 ? "pb-32" : "pb-24"}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">
              {multiSelectMode ? `Seleziona consegne (${multiSelectedIds.length})` : `Consegne attive (${sortedActive.length})`}
            </p>
            {multiSelectMode && multiSelectedIds.length !== sortedActive.length && (
              <button onClick={() => setMultiSelectedIds(sortedActive.map((j) => j.id))} className="text-[10px] font-bold text-[#FF5200] uppercase" data-testid="select-all-btn">
                Seleziona tutti
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {sortedActive.map((job) => {
              const isSelected = multiSelectedIds.includes(job.id);
              return (
                <div key={job.id} className={`w-full border rounded p-3 flex items-center justify-between gap-2 ${isSelected ? "bg-orange-50 border-[#FF5200]" : "bg-white border-zinc-200"}`} data-testid={`home-job-${job.tracking_number}`}>
                  {multiSelectMode && (
                    <button
                      type="button"
                      onClick={() => toggleMultiSelect(job.id)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-[#FF5200] border-[#FF5200]" : "bg-white border-zinc-300"}`}
                      data-testid={`multi-select-${job.tracking_number}`}
                      aria-label="Seleziona per consegna multipla"
                    >
                      {isSelected && <Check size={14} className="text-white" strokeWidth={3} />}
                    </button>
                  )}
                  <button
                    onClick={() => { if (multiSelectMode) { toggleMultiSelect(job.id); } else { navigateTo("detail", job); } }}
                    className="flex-1 flex items-center gap-2 text-left min-w-0"
                  >
                    <div className={`w-1 h-8 rounded-full ${sColor(job.status)}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#09090B] uppercase truncate">{getPrimaryStopSummary(job).title}</p>
                      {getPrimaryStopSummary(job).address && <p className="text-[11px] text-zinc-700 truncate">{getPrimaryStopSummary(job).address}</p>}
                      <p className="text-[11px] text-zinc-500 truncate">{getPrimaryStopSummary(job).locality}</p>
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        {myLocation && etaBadge(job) && <span className="text-[10px] font-bold text-[#FF5200]" data-testid={`job-distance-${job.tracking_number}`}>{etaBadge(job)}</span>}
                        <p className="text-[10px] text-zinc-400 truncate">{job.tracking_number}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <UrgencyBadge job={job} context="home-list" />
                        {getReadyBadgeLabel(job) && <span className="px-2 py-1 bg-green-50 text-green-700 text-[11px] font-bold rounded-sm" data-testid={`driver-ready-badge-${job.tracking_number}`}>{getReadyBadgeLabel(job)}</span>}
                        {getDdtBadgeLabel(job) && <button type="button" onClick={(e) => { e.stopPropagation(); downloadPdf(job.ddt_files[0]); }} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 text-[11px] font-bold rounded-sm hover:bg-zinc-200" data-testid={`driver-ddt-badge-${job.tracking_number}`}><Paperclip size={12} /> {getDdtBadgeLabel(job)}</button>}
                      </div>
                    </div>
                  </button>
                  {!multiSelectMode && (
                    <div className="flex items-center gap-2">
                      {getPrimaryStopDetails(job) && (
                        <a href={mapsUrl(getPrimaryStopDetails(job), myLocation ? [myLocation.lat, myLocation.lng] : null)} target="_blank" rel="noopener noreferrer" className="h-9 w-9 flex items-center justify-center border border-zinc-200 rounded-sm text-[#09090B] bg-white" data-testid={`navigate-job-${job.tracking_number}`}>
                          <Navigation size={14} />
                        </a>
                      )}
                      <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase text-white rounded-full ${sColor(job.status)}`}>{sLabel(job.status)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Multi-select floating action bar */}
      {multiSelectMode && multiSelectedIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#09090B] border-t border-zinc-800 p-3 z-40 flex items-center gap-3" data-testid="multi-pod-actionbar">
          <div className="flex-1 text-white">
            <p className="text-xs uppercase tracking-widest text-zinc-400">Selezionate</p>
            <p className="text-lg font-bold">{multiSelectedIds.length} {multiSelectedIds.length === 1 ? "consegna" : "consegne"}</p>
          </div>
          <button
            onClick={startMultiPod}
            disabled={multiSelectedIds.length === 0}
            className="h-12 px-4 bg-green-600 text-white font-bold uppercase text-sm rounded-sm flex items-center gap-2 disabled:opacity-40"
            data-testid="start-multi-pod-btn"
          >
            <Check size={16} /> Chiudi con firma
          </button>
        </div>
      )}
    </div>
  );
}
